import { create } from "zustand";
import { persist } from "zustand/middleware";

type Theme = "light" | "dark" | "system";

interface ThemeStore {
  theme: Theme;
  set: (t: Theme) => void;
}

/** Resolve system preference to light/dark. */
function resolveSystem(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Apply the resolved theme class to <html>. */
function applyTheme(theme: Theme) {
  const resolved = theme === "system" ? resolveSystem() : theme;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: "system",
      set: (t) => {
        set({ theme: t });
        applyTheme(t);
      },
    }),
    {
      name: "nhatminh-theme",
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

/**
 * Call once at app startup (after store rehydrates) to wire system-preference
 * changes when theme === "system".
 */
export function initThemeListener() {
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => {
    if (useThemeStore.getState().theme === "system") {
      applyTheme("system");
    }
  };
  mq.addEventListener("change", handler);
  return () => mq.removeEventListener("change", handler);
}
